import dayjs from 'libs/dayjs';

/**
 * Fecha de hoy en horario de Chile, como string 'YYYY-MM-DD'.
 * Referencia horaria única para agrupar/ordenar tareas por vencimiento.
 */
export const hoyEnSantiago = () => dayjs().tz('America/Santiago').format('YYYY-MM-DD');

/**
 * Fecha de referencia de una tarea para clasificar/ordenar por vencimiento.
 * El backend devuelve tareas que tienen `planned_start` O `planned_end`
 * (`order.srv.ts:listTareasDelTecnico`) — una tarea que ya arrancó pero
 * todavía no tiene fecha de término debe seguir siendo visible y clasificada
 * por su inicio, no caer en 'sinFecha'.
 */
export const fechaReferencia = (tarea) => tarea.planned_end ?? tarea.planned_start ?? null;

/**
 * Estado de vencimiento de una tarea respecto a `hoy` ('YYYY-MM-DD'), usando
 * `planned_end ?? planned_start` como fecha de referencia.
 * - 'vencida': referencia < hoy y no está terminada (finish).
 * - 'terminada': referencia < hoy y está terminada (finish).
 * - 'estaSemana': referencia entre hoy y hoy+7 días (inclusive).
 * - 'proxima': referencia después de hoy+7 días.
 * - 'sinFecha': no tiene ni planned_end ni planned_start.
 */
export const estadoDeVencimiento = (tarea, hoy) => {
  const { finish } = tarea;
  const ref = fechaReferencia(tarea);
  if (!ref) return 'sinFecha';
  if (ref < hoy) return finish ? 'terminada' : 'vencida';
  const limiteSemana = dayjs(hoy).add(7, 'day').format('YYYY-MM-DD');
  if (ref <= limiteSemana) return 'estaSemana';
  return 'proxima';
};

/**
 * Compara dos ids de forma robusta ante mezcla de tipos (number vs string,
 * según de dónde vengan: JWT decodificado vs fila de MySQL). `null`/`undefined`
 * nunca son iguales entre sí ni a ningún id real.
 */
const mismoId = (a, b) => {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return Number(a) === Number(b);
};

/**
 * Determina si una tarea es del técnico logueado (`miUserId`).
 * El backend (`order.srv.ts:listTareasDelTecnico`, ver `order.routes.js`)
 * ahora trae las tareas planificadas de TODAS las ODT activas del tenant —
 * el frontend filtra a "las mías" con esta regla:
 * - La tarea tiene responsable propio (`assigned_to_user_id`) → soy yo si coincide.
 * - La tarea NO tiene responsable propio (null/undefined) → soy yo si soy el
 *   responsable de la ODT completa (`order_assigned_to_user_id`).
 * @param {{assigned_to_user_id?: number|string|null, order_assigned_to_user_id?: number|string|null}} tarea
 * @param {number|string|null|undefined} miUserId
 * @returns {boolean}
 */
export const esMiTarea = (tarea, miUserId) => {
  if (miUserId === null || miUserId === undefined) return false;
  const { assigned_to_user_id: responsableTarea, order_assigned_to_user_id: responsableOdt } =
    tarea;
  if (responsableTarea !== null && responsableTarea !== undefined) {
    return mismoId(responsableTarea, miUserId);
  }
  return mismoId(responsableOdt, miUserId);
};

const ordenarPorFecha = (tareas) =>
  [...tareas].sort((a, b) => {
    const refA = fechaReferencia(a);
    const refB = fechaReferencia(b);
    return refA < refB ? -1 : refA > refB ? 1 : 0;
  });

/**
 * Agrupa y ordena las tareas del técnico según su vencimiento respecto a `hoy`.
 * @param {Array} tareas
 * @param {string} hoy - 'YYYY-MM-DD'
 */
export const agruparPorVencimiento = (tareas, hoy) => {
  const grupos = { vencidas: [], estaSemana: [], proximas: [], sinFecha: [], terminadas: [] };

  tareas.forEach((tarea) => {
    const estado = estadoDeVencimiento(tarea, hoy);
    if (estado === 'vencida') grupos.vencidas.push(tarea);
    else if (estado === 'estaSemana') grupos.estaSemana.push(tarea);
    else if (estado === 'proxima') grupos.proximas.push(tarea);
    else if (estado === 'terminada') grupos.terminadas.push(tarea);
    else grupos.sinFecha.push(tarea);
  });

  grupos.vencidas = ordenarPorFecha(grupos.vencidas);
  grupos.estaSemana = ordenarPorFecha(grupos.estaSemana);
  grupos.proximas = ordenarPorFecha(grupos.proximas);
  grupos.terminadas = ordenarPorFecha(grupos.terminadas);

  return grupos;
};
